import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePaginatedApi, useFormSubmit, useAsyncOperation, useApi } from '../hooks/useApi';
import { trucksAPI, clientsAPI, transporteursAPI, driversAPI, systemConfigAPI } from '../services/api';
import { CLIENT_FLEET_MAINTENANCE_CONFIG_KEY } from '../constants/systemConfigKeys';
import { formatDate } from '../utils/helpers';
import Table from '../components/Table';
import Button from '../components/Button';
import Input from '../components/Input';
import MultiSelect from '../components/MultiSelect';
import Modal, { ConfirmModal } from '../components/Modal';
import { useAuth } from '../contexts/AuthContext';
import { TruckIcon, PlusIcon, EyeIcon, PencilIcon, TrashIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';

const TrucksPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isAdmin, isManagerOrAdmin, isOperator, user, getOperatorClient } = useAuth();
  const isClientUser = isOperator();
  const { data: clientFleetMaintenanceConfig } = useApi(
    isClientUser ? () => systemConfigAPI.getByKey(CLIENT_FLEET_MAINTENANCE_CONFIG_KEY) : null
  );
  const clientFleetMaintenanceEnabled = Boolean(clientFleetMaintenanceConfig?.value ?? clientFleetMaintenanceConfig?.data?.value);
  const canMaintainTrucks = isManagerOrAdmin() || (isClientUser && clientFleetMaintenanceEnabled);
  

  const [filters, setFilters] = useState({ search: '' });
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedTruck, setSelectedTruck] = useState(null);
  
  const {
    data: trucks,
    pagination,
    loading,
    updateParams,
    goToPage,
    refresh
  } = usePaginatedApi(trucksAPI.getAll, filters);

  // Fetch clients for filter; transporteurs and drivers for the form
  const { data: clientsData } = useApi(() => clientsAPI.getAll({ limit: 500 }));
  const clients = clientsData?.clients || [];
  const { data: transporteursData, loading: transporteursLoading } = useApi(() => transporteursAPI.getAll({ limit: 500 }));
  const transporteurs = transporteursData?.transporteurs || [];
  const { data: driversData, loading: driversLoading } = useApi(() => driversAPI.getAll({ limit: 500 }));
  const drivers = driversData?.drivers || [];
  




  // Form submission hooks
  const { submit: submitCreate, loading: createLoading } = useFormSubmit(
    trucksAPI.create,
    {
      onSuccess: () => {
        setIsAddModalOpen(false);
        refresh();
      },
      successMessage: t('trucks.crud.truckCreated'),
    }
  );

  const { submit: submitUpdate, loading: updateLoading } = useFormSubmit(
    (data) => trucksAPI.update(selectedTruck.id, data),
    {
      onSuccess: () => {
        setIsEditModalOpen(false);
        setSelectedTruck(null);
        refresh();
      },
      successMessage: t('trucks.crud.truckUpdated'),
    }
  );

  const { execute: executeDelete, loading: deleteLoading } = useAsyncOperation();

  // Ref to store the debounce timeout ID
  const searchTimeoutRef = useRef(null);

  const handleSearchChange = (value) => {
    const newFilters = { search: value };
    setFilters(newFilters);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      updateParams(newFilters);
    }, 300);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Event handlers
  const handleEdit = (truck) => {
    setSelectedTruck(truck);
    setIsEditModalOpen(true);
  };

  const handleView = (truck) => {
    navigate(`/transporteurs/trucks/${truck.id}`);
  };

  const handleDelete = (truck) => {
    setSelectedTruck(truck);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedTruck?.id) return;
    try {
      await executeDelete(
        () => trucksAPI.delete(selectedTruck.id),
        { showSuccessToast: true, successMessage: t('trucks.crud.truckDeleted') }
      );
      setIsDeleteModalOpen(false);
      setSelectedTruck(null);
      refresh();
    } catch {
      /* errors handled in executeDelete */
    }
  };

  // Define all columns
  const allColumns = [
    {
      key: 'plateNumber',
      title: t('trucks.plateNumber'),
      sortable: true,
      render: (value) => (
        <div className="font-medium text-gray-900">{value}</div>
      ),
    },
    {
      key: 'drivers',
      title: t('trucks.driverName'),
      render: (value, row) => {
        const list = Array.isArray(row.drivers) && row.drivers.length > 0
          ? row.drivers
          : (row.assignedDrivers || []).map((dt) => dt.driver).filter(Boolean);
        if (list.length > 0) {
          return (
            <div className="space-y-1">
              {list.map((d, index) => (
                <div key={d.id || index} className="text-sm text-gray-900">
                  {d.firstName} {d.lastName}
                  {d.phone && <span className="text-gray-500 ml-1">• {d.phone}</span>}
                </div>
              ))}
            </div>
          );
        }
        return (
          <div className="text-sm text-gray-400 italic">
            {t('trucks.noDriversAssigned', 'No drivers assigned')}
          </div>
        );
      },
    },
    {
      key: 'transporteurs',
      title: t('trucks.transporteurs'),
      render: (value) => (
        <div className="text-sm text-gray-900">
          {Array.isArray(value) && value.length > 0
            ? value.map((t) => t.companyName).join(', ')
            : '—'}
        </div>
      ),
    },
    {
      key: 'status',
      title: t('trucks.status'),
      render: (value) => (
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
          value === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {value === 'active' ? t('trucks.active') : t('trucks.inactive')}
        </span>
      ),
    },
    {
      key: '_count',
      title: t('trucks.loads'),
      render: (value) => (
        <div className="text-sm text-gray-900">{value?.loads || 0}</div>
      ),
    },
    {
      key: 'createdAt',
      title: t('tables.date'),
      render: (value) => formatDate(value),
    },
    {
      key: 'actions',
      title: t('tables.actions'),
      sticky: true,
      width: '150px',
      render: (value, row) => (
        <div className="flex items-center space-x-2">
          <Button size="sm" variant="ghost" onClick={() => handleView(row)} title={t('common.view')}>
            <EyeIcon className="h-4 w-4" />
          </Button>
          {canMaintainTrucks && (
            <Button size="sm" variant="ghost" onClick={() => handleEdit(row)} title={t('common.edit')}>
              <PencilIcon className="h-4 w-4" />
            </Button>
          )}
          {canMaintainTrucks && (isAdmin() || (isClientUser && row.clients?.some(c => c.id === user?.clientId))) && (
            <Button 
              size="sm" 
              variant="ghost" 
              onClick={() => handleDelete(row)} 
              title={
                row._count?.loads > 0 
                  ? t('trucks.tooltips.cannotDeleteWithLoads')
                  : t('common.delete')
              }
              disabled={row._count?.loads > 0}
              className={
                row._count?.loads > 0 
                  ? 'text-gray-400 cursor-not-allowed' 
                  : 'text-red-600 hover:text-red-700'
              }
            >
              <TrashIcon className="h-4 w-4" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  // Filter columns based on user role - hide client column for clients
  const columns = isClientUser 
    ? allColumns.filter(column => column.key !== 'client')
    : allColumns;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <TruckIcon className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('trucks.title')}</h1>
            <p className="text-gray-600">{t('trucks.subtitle')}</p>
          </div>
        </div>
        
        {canMaintainTrucks && (
          <Button onClick={() => setIsAddModalOpen(true)}>
            <PlusIcon className="h-4 w-4 mr-2" />
            {t('trucks.addTruck')}
          </Button>
        )}
      </div>

      <div className="card">
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('trucks.filterPlateOrDriver')}
          </label>
          <div className="relative max-w-xl">
            <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
            <Input
              placeholder={t('trucks.filterPlateOrDriverPlaceholder')}
              value={filters.search}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="pl-10"
              aria-label={t('trucks.filterPlateOrDriver')}
            />
          </div>
        </div>

        <Table
          columns={columns}
          data={trucks}
          loading={loading}
          pagination={pagination}
          onPageChange={goToPage}
          emptyMessage={t('messages.noDataFound')}
        />
      </div>

      {/* Add Modal */}
      <TruckForm
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSubmit={submitCreate}
        loading={createLoading}
        clients={clients}
        transporteurs={transporteurs}
        transporteursLoading={transporteursLoading}
        drivers={drivers}
        driversLoading={driversLoading}
        title={t('trucks.addNewTruck')}
        submitText={t('common.create')}
        isOperator={isClientUser}
        operatorClientId={isClientUser ? (getOperatorClient()?.id || user?.clientId) : null}
      />

      {/* Edit Modal */}
      <TruckForm
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedTruck(null);
        }}
        onSubmit={submitUpdate}
        loading={updateLoading}
        clients={clients}
        transporteurs={transporteurs}
        transporteursLoading={transporteursLoading}
        drivers={drivers}
        driversLoading={driversLoading}
        title={t('trucks.editTruckTitle')}
        submitText={t('common.update')}
        initialData={selectedTruck}
        isOperator={isClientUser}
        operatorClientId={isClientUser ? (getOperatorClient()?.id || user?.clientId) : null}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedTruck(null);
        }}
        onConfirm={confirmDelete}
        loading={deleteLoading}
        title={t('trucks.deleteTruckTitle')}
        message={t('trucks.crud.deleteConfirm', { plateNumber: selectedTruck?.plateNumber })}
        confirmText={t('common.delete')}
        variant="danger"
      />
    </div>
  );
};

// Truck Form Component
const TruckForm = ({
  isOpen,
  onClose,
  onSubmit,
  loading,
  clients,
  transporteurs = [],
  transporteursLoading = false,
  drivers = [],
  driversLoading = false,
  title,
  submitText,
  initialData = {},
  isOperator = false,
  operatorClientId = null,
}) => {
  const { t } = useTranslation();
  
  const [formData, setFormData] = useState({
    plateNumber: '',
    transporteurIds: [],
    driverIds: [],
    emptyWeight: '',
    status: 'active',
  });

  React.useEffect(() => {
    if (isOpen) {
      setFormData({
        plateNumber: initialData?.plateNumber || '',
        transporteurIds: initialData?.transporteurs?.map((t) => t.id).filter(Boolean) || [],
        driverIds: initialData?.drivers?.map((d) => d.id).filter(Boolean) || [],
        emptyWeight: initialData?.emptyWeight ? String(initialData.emptyWeight / 1000) : '',
        status: initialData?.status || 'active',
      });
    } else {
      setFormData({
        plateNumber: '',
        transporteurIds: [],
        driverIds: [],
        emptyWeight: '',
        status: 'active',
      });
    }
  }, [isOpen, initialData?.id, initialData?.drivers]); // eslint-disable-line react-hooks/exhaustive-deps -- initialize form values when opening or switching the edited truck

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.plateNumber) {
      alert(t('trucks.plateNumberRequired'));
      return;
    }
    const submitData = {
      plateNumber: formData.plateNumber.toUpperCase(),
      transporteurIds: Array.isArray(formData.transporteurIds) ? formData.transporteurIds.filter((id) => id && String(id).trim()) : [],
      driverIds: Array.isArray(formData.driverIds) ? formData.driverIds.filter((id) => id && String(id).trim()) : [],
    };

    // Only include emptyWeight if user is not an operator and has provided a value
    if (!isOperator && formData.emptyWeight && String(formData.emptyWeight).trim() !== '') {
      submitData.emptyWeight = parseFloat(formData.emptyWeight) * 1000;
    } else {
      // Remove emptyWeight from submitData if it's empty or if user is operator
      delete submitData.emptyWeight;
    }

    onSubmit(submitData);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="lg"
      closeOnOverlayClick={false}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" loading={loading} form="truck-form">
            {submitText}
          </Button>
        </>
      }
    >
      <form id="truck-form" onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Plate Number */}
          <div>
            <Input
              label={t('trucks.plateNumber')}
              name="plateNumber"
              value={formData.plateNumber}
              onChange={handleChange}
              placeholder={t('trucks.plateNumberPlaceholder')}
              required
            />
          </div>

          {/* Transporteurs / Carriers – clients are defined by which transporteurs (carriers) work with which clients */}
          <div className="md:col-span-2">
              <MultiSelect
                label={`${t('trucks.transporteurs')} (${t('common.optional')})`}
                name="transporteurIds"
                values={formData.transporteurIds || []}
                onChange={handleChange}
                placeholder={transporteursLoading ? t('common.loading') : t('trucks.selectTransporteurs')}
                options={transporteurs.map((tr) => ({
                  value: tr.id,
                  label: tr.companyName
                }))}
                disabled={transporteursLoading}
              />
            </div>

          {/* Assigned Drivers (Driver entity - from Transporteurs > Drivers) */}
            <div className="md:col-span-2">
              <MultiSelect
                label={`${t('trucks.form.assignedDriver')} (${t('common.optional')})`}
                name="driverIds"
                values={formData.driverIds || []}
                onChange={handleChange}
                disabled={driversLoading}
                placeholder={driversLoading ? t('common.loading') : t('trucks.form.selectDriver')}
                options={drivers.map((d) => ({
                  value: d.id,
                  label: `${d.firstName} ${d.lastName}${d.transporteurs?.length ? ` — ${d.transporteurs.map(t => t.companyName).join(', ')}` : ''}`
                }))}
              />
              <p className="text-xs text-gray-500 mt-1">
                {t('trucks.form.assignedDriverInfo')}
              </p>
            </div>

          {/* Empty Weight */}
          {!isOperator && !initialData?.id ? (
            <div>
              <Input
                label={t('trucks.emptyWeight')}
                name="emptyWeight"
                type="number"
                value={formData.emptyWeight}
                onChange={handleChange}
                placeholder={t('trucks.emptyWeightPlaceholder')}
                min="0"
                step="0.01"
              />
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('trucks.emptyWeight')}
              </label>
              <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-500">
                {initialData?.emptyWeight ? `${(initialData.emptyWeight / 1000).toLocaleString('fr-FR', { maximumFractionDigits: 2, minimumFractionDigits: 2 })} Ton` : 'Non défini'}
              </div>
              {isOperator && (
                <p className="text-xs text-gray-500 mt-1">
                  {t('trucks.emptyWeightRestricted')}
                </p>
              )}
            </div>
          )}

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('trucks.status')} *
            </label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              required
            >
              <option value="active">{t('trucks.active')}</option>
              <option value="inactive">{t('trucks.inactive')}</option>
            </select>
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default TrucksPage;